import type { LocationQuery, RouteLocationRaw } from 'vue-router'

export type CommitmentStep = 1 | 2 | 3
export type CommitmentStepName = 'parents' | 'children' | 'confirmation'

const TOTAL_FORM_STEPS = 2

const STEP_SLUGS: Record<CommitmentStep, string | null> = {
  1: null,
  2: 'children',
  3: 'confirmation',
}

const STEP_NAMES: Record<CommitmentStep, CommitmentStepName> = {
  1: 'parents',
  2: 'children',
  3: 'confirmation',
}

const STEP_SUFFIX = /\/(children|confirmation)$/

export function commitmentStepFromParam(
  param: string | string[] | undefined,
): CommitmentStep {
  const slug = Array.isArray(param) ? param[0] : param
  if (slug === 'children') return 2
  if (slug === 'confirmation') return 3
  return 1
}

// Built from the current path rather than the route name so a visit that
// started on the /pledge alias stays on it. The one-time admin `token` is
// stripped from the address bar on mount, which leaves route.query stale, so
// it is dropped here too rather than put back into history.
export function commitmentStepLocation(
  path: string,
  query: LocationQuery,
  step: CommitmentStep,
): RouteLocationRaw {
  const base = path.replace(STEP_SUFFIX, '')
  const slug = STEP_SLUGS[step]
  const { token: _token, ...rest } = query
  return { path: slug ? `${base}/${slug}` : base, query: rest }
}

export const useCommitmentProgress = () =>
  useState('commitment-progress', () => ({
    reached: 1 as CommitmentStep,
    completed: false,
  }))

interface CommitmentStepsOptions {
  scrollTarget: Ref<HTMLElement | null>
}

export function useCommitmentSteps({ scrollTarget }: CommitmentStepsOptions) {
  const route = useRoute()
  const progress = useCommitmentProgress()

  // Browser-back from the confirmation lands on step 1 of a finished
  // submission; the page registers how to clear the form for a fresh one.
  let restart = () => {}
  const onRestart = (callback: () => void) => {
    restart = callback
  }

  const step = computed(() => commitmentStepFromParam(route.params.step))
  const stepName = computed(() => STEP_NAMES[step.value])
  const isLastStep = computed(() => step.value === TOTAL_FORM_STEPS)
  const showSuccessPage = computed(() => step.value > TOTAL_FORM_STEPS)

  const reset = () => {
    progress.value = { reached: 1, completed: false }
  }

  const go = (target: CommitmentStep) =>
    navigateTo(commitmentStepLocation(route.path, route.query, target))

  const next = () => {
    progress.value.reached = Math.max(
      progress.value.reached,
      2,
    ) as CommitmentStep
    return go(2)
  }

  const back = () => go(1)

  const complete = () => {
    progress.value = { reached: 3, completed: true }
    return go(3)
  }

  // Steps swap in place, so without this the next step opens scrolled to
  // wherever the last one ended. On lg and up the pledge layout scrolls its
  // own column rather than the window, so reset whichever ancestor actually
  // holds the scroll.
  watch(step, async (current) => {
    if (current === 1 && progress.value.completed) {
      reset()
      restart()
    }

    await nextTick()

    let element = scrollTarget.value?.parentElement

    while (element) {
      const { overflowY } = getComputedStyle(element)

      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        element.scrollHeight > element.clientHeight
      ) {
        element.scrollTo({ top: 0, behavior: 'smooth' })
        break
      }

      element = element.parentElement
    }

    window.scrollTo({ top: 0, behavior: 'smooth' })
  })

  onUnmounted(reset)

  return {
    step,
    stepName,
    isLastStep,
    showSuccessPage,
    next,
    back,
    complete,
    onRestart,
  }
}
